import { pluralEn, type Count } from '../lib/format'

import type { Translations } from './types'

/** « às 21:25 », but « à 01:25 »: the article before a clock time agrees with the hour. */
const atHour = (time: string): string => (/^0?1\D/.test(time) ? `à ${time}` : `às ${time}`)

/**
 * Portuguese (European). Typed against the Russian source: every key and every
 * function signature must match. Amounts and dates arrive already formatted;
 * the dictionary supplies only the words around them.
 */
export const pt: Translations = {
  common: {
    all: 'Máx.',
    back: 'Voltar',
    cancel: 'Cancelar',
    confirm: 'Confirmar',
    copy: 'Copiar',
    copied: 'Copiado',
    open: 'Abrir',
    retry: 'Tentar novamente',
    save: 'Guardar',
    close: 'Fechar',
    loading: 'A carregar',
    available: 'Disponível',
    total: 'Total',
    amount: 'Montante',
    network: 'Rede',
    asset: 'Moeda',
    status: 'Estado',
    date: 'Data',
    nothingYet: 'Ainda nada',
    somethingWentWrong: 'Algo correu mal',
    reload: 'Recarregar',
    crashHint: 'Não foi possível mostrar o ecrã. Recarregue a aplicação: os seus fundos e pedidos não são afetados.',
  },

  nav: {
    label: 'Navegação principal',
    home: 'Início',
    markets: 'Mercados',
    signals: 'Sinais',
    trade: 'Trading',
    wallet: 'Ativos',
  },

  auth: {
    signIn: 'Iniciar sessão',
    signInSubtitle: 'Entre para voltar aos seus sinais e ao seu saldo',
    signInAction: 'Entrar',
    signUp: 'Registo',
    signUpSubtitle: 'E-mail e palavra-passe: é tudo o que precisa para começar',
    signUpSubtitleInvite: 'E-mail, palavra-passe e código de convite: é tudo o que precisa para começar',
    email: 'E-mail',
    emailPlaceholder: 'nome@exemplo.com',
    password: 'Palavra-passe',
    passwordHint: 'Mínimo de 8 caracteres, com letras e números',
    passwordConfirm: 'Repita a palavra-passe',
    referralCode: 'Código de convite',
    referralCodePlaceholder: 'Por exemplo, 8K3M7QPL',
    referralCodeHint: 'Opcional: se foi convidado por um membro da 19CRT',
    referralCodeRequiredHint: 'Registo apenas por convite: use a ligação ou o código de quem o convidou',
    /** The programme's live terms under a prefilled invitation code: what the newcomer gets and for what. */
    referralTerms: (deposit: string, asset: string, signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `${signals.text} ${pluralEn(signals, 'sinal bónus', 'sinais bónus')}` : null,
        promo === null ? null : `um bónus promocional de ${promo} USDT`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0
        ? `Se o código lhe foi dado por um membro da 19CRT, o convite conta após um primeiro depósito de pelo menos ${deposit} ${asset}`
        : `Se o código lhe foi dado por um membro da 19CRT, após um primeiro depósito de pelo menos ${deposit} ${asset} recebe ${perks.join(' e ')}`
    },
    forgot: 'Esqueceu-se da palavra-passe?',
    createAccount: 'Criar conta',
    haveAccount: 'Já tem conta?',
    noAccount: 'Ainda não tem conta?',
    backToSignIn: 'Voltar ao início de sessão',
    verifyTitle: 'Confirme o e-mail',
    verifySent: (email: string) => `Enviámos um e-mail para ${email}`,
    verifySentAnywhere: 'Enviámos um e-mail com a ligação para o seu endereço',
    verifyHint:
      'Abra a ligação do e-mail para confirmar o endereço: é para lá que vão os e-mails de recuperação da palavra-passe. Se não a vir, verifique a pasta «Spam».',
    verifyEmailPrompt: 'Indique o e-mail com que registou a conta',
    verifyResend: 'Enviar o e-mail novamente',
    verifyResendIn: (seconds: Count) => `Pode reenviar dentro de ${seconds.text} s`,
    verifyResent: 'Enviámos outro e-mail',
    verifyResentMaybe: 'Se o endereço estiver registado e ainda não confirmado, um novo e-mail está a caminho',
    verifyChecking: 'A verificar a ligação',
    verifyDone: 'E-mail confirmado',
    verifyDoneHint: 'A abrir a aplicação',
    verifyDoneSignInHint: 'A abrir o início de sessão',
    resetTitle: 'Recuperar palavra-passe',
    resetSubtitle: 'Indique o e-mail: enviaremos uma ligação para mudar a palavra-passe',
    resetSend: 'Enviar ligação',
    resetSent: 'Se essa conta existir, o e-mail já foi enviado',
    resetSentHint:
      'Não indicamos se um endereço está registado: caso contrário, este formulário permitiria verificar e-mails alheios. Veja em «Recebidos» e em «Spam».',
    newPassword: 'Nova palavra-passe',
    newPasswordSubtitle: 'Escolha uma nova palavra-passe. A anterior deixa de funcionar de imediato.',
    newPasswordSubmit: 'Guardar palavra-passe',
    requestNewLink: 'Pedir nova ligação',
    newPasswordDone: 'Palavra-passe atualizada',
    newPasswordDoneHint: 'A abrir o início de sessão',
    signOut: 'Terminar sessão',
    totpCode: 'Código de confirmação',
    totpHint: 'Os seis dígitos da sua aplicação de autenticação',
    recoveryCode: 'Código de recuperação',
    recoveryCodeHint: 'Oito caracteres dos códigos que guardou ao ativar a 2FA',
    useRecoveryCode: 'Usar um código de recuperação',
    useAppCode: 'Usar a aplicação de autenticação',
    resetLinkInvalid: 'A ligação não funciona',
    errors: {
      invalidCredentials: 'E-mail ou palavra-passe incorretos',
      emailTaken: 'Este e-mail já está registado',
      weakPassword: 'A palavra-passe é demasiado curta',
      passwordRule: 'A palavra-passe deve conter letras e números',
      passwordsDiffer: 'As palavras-passe não coincidem',
      invalidEmail: 'Introduza um endereço no formato nome@exemplo.com',
      invalidReferralCode: 'Verifique o código de convite',
      referralRequired: 'Introduza o código de convite',
      emailRequired: 'Indique o e-mail',
      passwordRequired: 'Introduza a palavra-passe',
      accountBlocked: 'O acesso à conta está limitado. Contacte o apoio',
      rateLimited: 'Demasiadas tentativas. Tente mais tarde',
      totpRequired: 'Introduza o código da aplicação de autenticação',
      totpInvalid: 'O código não foi aceite. Verifique a hora do telemóvel e introduza um novo',
      totpFormat: 'O código da aplicação tem seis dígitos, um código de recuperação tem oito caracteres',
      expiredToken: 'A ligação expirou. Peça uma nova',
      verifyLinkUsed: 'Esta ligação já foi usada. Inicie sessão ou abra o e-mail mais recente, se pediu um novo',
      missingToken: 'A ligação está incompleta. Abra-a por inteiro a partir do e-mail',
      validation: 'Verifique os campos preenchidos',
      network: 'Sem ligação ao servidor. Verifique a sua rede',
    },
  },

  home: {
    promoLabel: 'Promoções',
    banners: [
      {
        title: 'Sinais sobre o ouro',
        subtitle: 'Um código do nosso canal de Telegram: uma operação num só passo',
        cta: 'Introduzir código',
      },
      {
        title: 'Mercados em tempo real',
        subtitle: 'Gráficos e livro de ordens ao vivo para todos os pares',
        cta: 'Abrir mercados',
      },
      {
        title: 'Deposite em minutos',
        subtitle: 'USDT nas redes TRC20 e ERC20',
        cta: 'Carregar a conta',
      },
    ],
    pauseAnnouncements: 'Pausar os anúncios',
    resumeAnnouncements: 'Retomar os anúncios',
    announcementsLabel: 'Anúncios',
    announcements: [
      'Antes de depositar, verifique a moeda e a rede da transferência',
      'Gira a proteção da conta na secção «Segurança»',
      'O histórico de operações está na secção «Ativos»',
    ],
    totalBalance: 'Saldo total',
    quickTrade: 'Trading rápido',
    quickTradeHint: 'Spot e futuros num único terminal',
    quickActions: 'Ações rápidas',
    signalLive: 'Sinal disponível',
    signalNext: 'Próximo sinal',
    signalClosesIn: 'fecha dentro de',
    signalOpensAt: (time: string) => `abre ${atHour(time)}`,
    signalsTitle: 'Sinais sobre o ouro',
    allSignals: 'Todos os sinais',
    marketMoves: 'Movimento do mercado',
    pair: 'Par',
    lastPrice: 'Preço',
    change24h: 'Variação 24 h',
    /** After the figure in the home balance's change pill: « +1.30% em 24 h ». */
    change24hShort: 'em 24 h',
    volume24h: 'Volume 24 h',
    allMarkets: 'Todos os mercados',
    pricesUnavailable: 'Cotações indisponíveis: não conseguimos avaliar toda a carteira',
    ratesUnavailable: 'Cotações temporariamente indisponíveis',
    marketsUnavailable: 'A lista de mercados está temporariamente indisponível',
    signalsNone: 'Não há sinais neste momento: os novos códigos saem no nosso canal de Telegram',
  },

  markets: {
    title: 'Mercados',
    search: 'Procurar par',
    spot: 'Spot',
    futures: 'Futuros',
    gainers: 'Maiores subidas',
    losers: 'Descidas',
    volume: 'Volume 24 h',
    volumeShort: 'volume',
    typeLabel: 'Tipo de mercado',
    sortLabel: 'Ordenação',
    clearSearch: 'Limpar pesquisa',
    favourites: 'Favoritos',
    addFavourite: 'Adicionar aos favoritos',
    removeFavourite: 'Remover dos favoritos',
    emptyFavourites: 'Ainda não há favoritos: marque pares com a estrela',
    empty: 'Nada encontrado',
    emptyHint: 'Verifique o símbolo do par, por exemplo BTC ou SOL.',
    emptySpot: 'Ainda não há pares spot abertos',
    emptyFutures: 'Ainda não há pares de futuros abertos',
    loadFailed: 'Não foi possível carregar a lista de mercados',
    pricesUnavailable: 'Cotações temporariamente indisponíveis. Os preços serão atualizados automaticamente.',
  },
  assets: {
    /** Asset names the interface translates; any other asset keeps the name the server sends. */
    names: { XAU: 'Ouro' } as Record<string, string>,
  },

  wallet: {
    title: 'Ativos',
    totalValue: 'Valor total da conta',
    accounts: 'As minhas contas',
    latestWithdrawal: 'Último levantamento',
    coin: 'Moeda',
    searchCoin: 'Pesquisar uma moeda',
    quickActions: 'Ações rápidas',
    coins: 'Moedas',
    hideBalance: 'Ocultar saldo',
    showBalance: 'Mostrar saldo',
    loadError: 'Não foi possível carregar o saldo',
    pricesUnavailable: 'Cotações indisponíveis: não é possível avaliar toda a carteira',
    coinsEmpty: 'Ainda não há moedas. Carregue a conta e elas aparecerão aqui.',
    accountSpot: 'Conta spot',
    accountSpotHint: 'Disponível para negociar',
    accountFutures: 'Conta de futuros',
    accountFuturesHint: 'Margem das posições',
    accountCopy: 'Copy trading (arquivado)',
    accountCopyHint: 'Transfira para o spot',
    actions: {
      deposit: 'Depositar',
      withdraw: 'Levantar',
      transfer: 'Transferir',
      history: 'Histórico',
    },
    deposit: {
      title: 'Depósito',
      history: 'Histórico de depósitos',
      chain: 'Rede',
      saveQr: 'Guardar código QR',
      address: 'Endereço de depósito',
      copyAddress: 'Copiar endereço',
      notice: 'Aviso importante',
      noticeWrongNetwork: (asset: string, network: string) =>
        `Envie apenas ${asset} pela rede ${network}. Fundos enviados por outra rede serão perdidos.`,
      noticeConfirmations: (count: Count, minutes: Count) =>
        `Creditado após ${count.text} ${pluralEn(count, 'confirmação', 'confirmações')} da rede, normalmente em cerca de ${minutes.text} ${pluralEn(minutes, 'minuto', 'minutos')}.`,
      noticeMinimum: (amount: string, asset: string) =>
        `Depósito mínimo: ${amount} ${asset}.`,
      waiting: 'À espera da transferência',
      incoming: 'Depósitos a caminho',
      incomingFirst: 'Visto na rede, à espera da primeira confirmação',
      incomingProgress: (done: Count, total: Count) => `Confirmações: ${done.text} de ${total.text}`,
      selectAsset: 'Escolha uma moeda',
      noAssets: 'O depósito está temporariamente indisponível. Tente mais tarde.',
      noNetworks: 'Não há redes disponíveis para esta moeda: escolha outra.',
      addressFailed: 'Não foi possível obter o endereço de depósito',
      noticePermanent: 'O endereço é permanente: use-o em todos os próximos depósitos.',
      qrSaved: 'Código QR guardado',
      qrFailed: 'Não foi possível guardar o código QR',
      paymentLink: 'Ligação para a carteira',
      paymentLinkHint: 'Abra-a na carteira — a rede e o endereço preenchem-se sozinhos.',
      copyLink: 'Copiar ligação',
    },
    withdraw: {
      title: 'Levantamento',
      history: 'Histórico de levantamentos',
      asset: 'Moeda do levantamento',
      selectAsset: 'Escolha uma moeda',
      network: 'Rede do levantamento',
      address: 'Endereço de levantamento',
      addressPlaceholder: (network: string) => `Introduza um endereço ${network}`,
      addressPaste: 'Colar endereço',
      addressFromBook: (label: string): string => `Confirmado no seu livro de endereços · ${label}`,
      paste: 'Colar',
      addressInvalid: (network: string) => `Não parece um endereço da rede ${network}`,
      addressWrongNetwork: (detected: string, selected: string) =>
        `É um endereço da rede ${detected}, mas está selecionada ${selected}`,
      amount: 'Montante do levantamento',
      amountPlaceholder: '0.00',
      networkFee: 'Taxa de rede',
      platformFee: (rate: string): string => `Taxa da plataforma (${rate}%)`,
      feeFree: 'atualmente sem taxa',
      feeDynamic: 'ao preço atual do gás',
      willReceive: 'Vai receber',
      notice: 'Aviso importante',
      noticeReview: 'Cada pedido é verificado antes do envio. Os fundos são reservados de imediato.',
      noticeTiming: 'Envio até 24 horas após a aprovação.',
      noticeMinimum: (amount: string, asset: string) =>
        `Levantamento mínimo: ${amount} ${asset}.`,
      noticeSupport:
        'Verifique o endereço por si: uma transferência para um endereço alheio não pode ser recuperada. O apoio analisa reclamações pelo número do pedido.',
      insufficient: 'Fundos insuficientes',
      noticeLimit: (remaining: string, limit: string, verified: boolean): string =>
        verified ? `Restam hoje: ${remaining} de ${limit} USDT.` : `Sem verificação: até ${limit} USDT por dia, restam ${remaining}. Faça a verificação para aumentar o limite.`,
      addressNotConfirmed: 'Levantamentos apenas para endereços da lista confirmados por e-mail',
      limitReached: (verified: boolean): string => (verified ? 'Limite diário de levantamento esgotado' : 'Limite diário sem verificação esgotado: faça a verificação no perfil'),
      belowMinimum: (amount: string, asset: string) => `Mínimo ${amount} ${asset}`,
      confirmTitle: 'Reveja o pedido',
      confirmSubtitle: 'Um levantamento enviado não pode ser cancelado. Verifique a rede e o endereço.',
      confirmAction: 'Confirmar levantamento',
      failed: 'Não foi possível criar o pedido',
      submitted: 'Pedido criado',
      submittedHint: 'Após a verificação, os fundos seguem para a rede. Acompanhe o estado nos pedidos de levantamento.',
      requestId: 'Número do pedido',
      statusPending: 'Em análise',
      toRequests: 'Para os pedidos de levantamento',
      empty: 'De momento não há moedas disponíveis para levantamento',
      loadFailed: 'Não foi possível carregar a lista de moedas',
      routeUnavailable: 'Os levantamentos nesta rede estão indisponíveis de momento — escolha outra rede',
      invalidAmount: 'O montante não serve: verifique a precisão e a taxa de rede',
      edit: 'Corrigir o pedido',
    },
    withdrawals: {
      title: 'Pedidos de levantamento',
      toAddress: (address: string): string => `para ${address}`,
      note: 'Os pedidos rejeitados ou falhados devolvem a reserva à sua conta. Abra um pedido para ver a transação ou o motivo.',
      empty: 'Ainda não há pedidos de levantamento. Cada pedido mostrará aqui o seu estado, da verificação à confirmação pela rede.',
      loadError: 'Não foi possível carregar os pedidos',
      details: 'Pedido de levantamento',
      created: 'Criado',
      transaction: 'Transação',
      openExplorer: 'Abrir no explorador',
      reason: 'Motivo',
      status: {
        pending: 'Em análise',
        approved: 'Aprovado',
        rejected: 'Recusado',
        broadcasting: 'A enviar',
        sent: 'Enviado',
        confirmed: 'Confirmado',
        failed: 'Falhou',
      },
      statusHint: {
        pending: 'O pedido está em verificação. O montante está reservado na conta.',
        approved: 'Verificado, à espera de envio para a rede.',
        rejected: 'O pedido foi recusado; a reserva voltou à conta.',
        broadcasting: 'A transação está a ser enviada para a rede.',
        sent: 'A transação está na rede, à espera de confirmação.',
        confirmed: 'A rede confirmou a transação. Os fundos chegaram.',
        failed: 'O envio falhou; a reserva voltou à conta.',
      },
    },
    transfer: {
      title: 'Transferência',
      from: 'De',
      fromAvailable: (amount: string): string => `De · ${amount} disponíveis`,
      to: 'Para',
      submit: 'Transferir',
      swap: 'Trocar as contas',
      selectFrom: 'Conta de origem',
      selectTo: 'Conta de destino',
      selectAsset: 'Escolha uma moeda',
      after: 'Após a transferência',
      insufficient: 'Fundos insuficientes na conta de origem',
      done: 'Transferência efetuada',
      hint: 'As transferências entre as suas contas são instantâneas e sem taxa.',
      empty: 'Ainda não há nada para transferir: carregue primeiro a conta.',
      failed: 'Não foi possível efetuar a transferência. Tente novamente',
    },
    history: {
      title: 'Histórico de operações',
      deposit: 'Depósito',
      withdrawal: 'Levantamento',
      trade: 'Operação',
      fee: 'Taxa',
      transfer: 'Transferência',
      pnl: 'Resultado',
      empty: 'Ainda não há operações. Aqui aparecerão depósitos, levantamentos e operações.',
      emptyFiltered: 'Não há operações para este filtro',
      loadError: 'Não foi possível carregar o histórico',
      today: 'Hoje',
      yesterday: 'Ontem',
      balanceAfter: (amount: string) => `saldo ${amount}`,
      onlyAccount: (account: string) => `Apenas ${account}`,
      resetAccount: 'Mostrar todas as contas',
      accountFilter: 'Conta',
      allAccounts: 'Todas as contas',
      withdrawalsLink: 'Estado dos pedidos de levantamento',
      badges: {
        credited: 'Creditado',
        sent: 'Enviado',
        fee: 'Taxa',
        reserved: 'Reservado',
        returned: 'Devolvido',
        released: 'Libertado',
        filled: 'Executado',
        settled: 'Apurado',
        liquidated: 'Liquidação',
        done: 'Concluído',
        adjusted: 'Ajustado',
        paid: 'Pago',
      },
      filters: {
        all: 'Todas',
        rewards: 'Bónus',
        deposits: 'Depósitos',
        withdrawals: 'Levantamentos',
        trades: 'Operações',
        transfers: 'Transferências',
      },
      types: {
        deposit: 'Depósito',
        withdrawal: 'Levantamento',
        withdrawal_fee: 'Taxa de levantamento',
        withdrawal_platform_fee: 'Taxa da plataforma no levantamento',
        withdrawal_lock: 'Reserva para levantamento',
        withdrawal_release: 'Devolução da reserva de levantamento',
        order_lock: 'Reserva para ordem limite',
        order_release: 'Devolução da reserva da ordem',
        bonus: 'Bónus promocional',
        trade_buy: 'Compra',
        trade_sell: 'Venda',
        trading_fee: 'Taxa de trading',
        realized_pnl: 'Resultado da operação',
        signal_programmed_profit: 'Lucro programado do sinal',
        funding: 'Financiamento',
        margin_lock: 'Reserva de margem',
        margin_release: 'Devolução de margem',
        liquidation: 'Liquidação',
        transfer_in: 'Transferência recebida',
        transfer_out: 'Transferência enviada',
        copy_allocation: 'Alocado à cópia',
        copy_release: 'Devolução da cópia',
        copy_commission: 'Comissão de copy trading',
        referral: 'Recompensa de referência',
        adjustment: 'Ajuste',
      },
    },
  },

  trade: {
    title: 'Trading',
    menu: {
      account: 'Conta',
      trading: 'Trading',
      more: 'Mais',
    },
    long: 'Long',
    short: 'Short',
    buy: 'Comprar',
    sell: 'Vender',
    marketOrder: 'A mercado',
    limitOrder: 'Limite',
    price: 'Preço',
    marketPrice: 'A mercado',
    quantity: 'Quantidade',
    leverage: 'Alavancagem',
    takeProfitStopLoss: 'Take-profit / stop-loss',
    positions: 'Posições',
    orders: 'Ordens',
    history: 'Histórico',
    copyTrading: 'Copiar operações',
    unrealizedPnl: 'P&L não realizado',
    unrealizedPnlShort: 'P&L',
    margin: 'Margem',
    entry: 'Entrada',
    size: 'Tamanho',
    liquidation: 'Liquidação',
    close: 'Fechar',
    orderBook: 'Livro de ordens',
    noPositions: 'Coloque uma ordem acima e a posição aparecerá aqui.',
    balance: 'Saldo',
    perpetual: 'Perpétuo',
    perpShort: 'Perp',
    fundingIn: (time: string) => `em ${time}`,
    position: 'Posição',
    closeAtMarket: 'Fechar a mercado',
    keepPosition: 'Manter a posição',
    placeBuyOrder: 'Colocar ordem de compra',
    placeSellOrder: 'Colocar ordem de venda',
    placeLongOrder: 'Colocar ordem long',
    placeShortOrder: 'Colocar ordem short',
    isolatedMargin: 'Margem isolada',
    crossMargin: 'Margem cruzada',
    markShort: 'Marcação',
    liqPrice: 'Preço liq.',
    noPositionsTitle: 'Não há posições abertas',
    noOrdersTitle: 'Não há ordens ativas',
    noHistoryTitle: 'Ainda não há operações',
    pair: 'Par',
    selectPair: 'Escolha um par',
    sideLabel: 'Direção',
    orderType: 'Tipo de ordem',
    sizeShare: 'Parte do disponível',
    quantityShort: 'Qtd.',
    takeProfit: 'Take-profit',
    stopLoss: 'Stop-loss',
    tabsLabel: 'As minhas operações',
    openChart: 'Gráfico',
    openLong: (symbol: string) => `Long ${symbol}`,
    openShort: (symbol: string) => `Short ${symbol}`,
    buyAsset: (symbol: string) => `Comprar ${symbol}`,
    sellAsset: (symbol: string) => `Vender ${symbol}`,
    enterQuantity: 'Indique a quantidade',
    insufficient: 'Fundos insuficientes na conta',
    belowMinNotional: (amount: string, asset: string) =>
      `Volume mínimo da ordem: ${amount} ${asset}`,
    bookEmpty: 'Ainda não há ordens no livro',
    bookUnavailable: 'Livro de ordens indisponível',
    tradingUnavailable:
      'O serviço de trading não responde: de momento não é possível enviar a ordem. Saldos, depósitos e levantamentos funcionam normalmente.',
    marketsFailed: 'Não foi possível carregar a lista de pares',
    noMarkets: 'Ainda não há pares: aparecerão à medida que os mercados abrirem.',
    confirmTitle: 'Reveja a ordem',
    confirmSubtitle: 'Uma ordem executada não pode ser cancelada.',
    marginRequired: 'Margem usada',
    cost: 'A pagar',
    liquidationEstimate: 'Liquidação estimada',
    submitted: 'Ordem enviada',
    submitFailed: 'Não foi possível enviar a ordem',
    futuresSoon: 'Os futuros estão temporariamente indisponíveis. O trading spot funciona.',
    marginMode: 'Margem',
    cross: 'Cruzada',
    isolated: 'Isolada',
    marginModeHint: (mode: string): string =>
      mode === 'cross'
        ? 'Cruzada: todo o saldo de futuros sustenta a posição'
        : 'Isolada: só arrisca a margem desta posição',
    reduceOnly: 'Apenas reduzir',
    futuresAccount: 'Conta de futuros',
    equity: 'Capital',
    marginUsed: 'Em margem',
    marginRatio: 'Nível de risco',
    funding: 'Financiamento',
    fundingRate: 'Taxa por 8 h',
    nextFunding: 'Próximo',
    fundingPaid: 'Financiamento',
    protection: 'Proteção',
    editProtection: 'TP / SL',
    protectionSheetTitle: 'Take-profit e stop-loss',
    protectionSheetSubtitle: 'O motor fecha a posição a preço de mercado assim que o preço tocar no nível.',
    clearLevel: 'Sem nível',
    addMargin: 'Adicionar margem',
    removeMargin: 'Retirar margem',
    marginDelta: 'Montante da margem',
    marginDeltaHint: 'Posição isolada: mais margem afasta a liquidação',
    addMarginKyc: 'Adicionar margem fica disponível quando os documentos forem aprovados. Já pode retirar margem, alterar o take-profit e o stop-loss ou fechar a posição.',
    saveProtection: 'Guardar',
    protectionSaved: 'Proteção atualizada',
    protectionFailed: 'Não foi possível atualizar a posição',
    protectionInvalid: 'O nível está do lado errado do preço de entrada',
    leverageTooHigh: 'A alavancagem excede o permitido para este par',
    leverageTier: (cap: number, notional: string) => `Para um volume de ${notional} USDT, alavancagem até ${cap}×`,
    futuresDisabled: 'As ordens de futuros não são aceites temporariamente',
    liquidated: 'Liquidada',
    restingToast: (symbol: string, price: string) => `Ordem limite ${symbol} a ${price} colocada, à espera do preço`,
    cancelledToast: 'Ordem cancelada, fundos devolvidos',
    costSell: 'A receber',
    costBasis: 'Custo',
    enterProtection: 'O nível de take profit ou stop loss não é um preço válido',
    leverageLocked: (leverage: number) => `A alavancagem está fixada em ${leverage}× pela posição aberta. Feche-a para a alterar`,
    marginReleased: 'Margem devolvida',
    realizedEstimate: 'Resultado estimado',
    addsPosition: (quantity: string, asset: string) => `Adiciona ${quantity} ${asset} à posição aberta`,
    reducesPosition: (quantity: string, asset: string) => `Reduz a posição aberta em ${quantity} ${asset}`,
    closesPosition: (quantity: string, asset: string) => `Fecha a posição aberta de ${quantity} ${asset}`,
    reversesPosition: (closeQuantity: string, openQuantity: string, asset: string) =>
      `Fecha a posição de ${closeQuantity} ${asset} e abre ${openQuantity} ${asset} no sentido oposto`,
    byAccount: 'em toda a conta',
    cancelledToastPlain: 'Ordem cancelada',
    fillBuy: 'Compra',
    fillSell: 'Venda',
    marginFloor: (floor: string) => `A margem não pode ficar abaixo da margem inicial de ${floor} USDT`,
    marginRemovable: (amount: string) => `É possível retirar até ${amount} USDT`,
    enterPrice: 'Indique o preço da ordem limite',
    markPrice: 'Preço de marcação',
    tpShort: 'TP',
    slShort: 'SL',
    priceStale: 'O preço está desatualizado: o fluxo de cotações está atrasado. Tente dentro de alguns segundos.',
    marketDisabled: 'A negociação deste par está suspensa',
    filledToast: (side: string, quantity: string, asset: string, price: string) =>
      `Ordem executada: ${side.toLocaleLowerCase('pt-PT')} ${quantity} ${asset} a ${price}`,
    closedToast: (symbol: string, pnl: string) => `${symbol} fechada, resultado ${pnl} USDT`,
    positionsFailed: 'Não foi possível carregar as posições',
    noOrders: 'Uma ordem limite espera aqui até ser executada.',
    ordersFailed: 'Não foi possível carregar as ordens',
    noHistory: 'A primeira operação executada aparecerá nesta lista.',
    historyFailed: 'Não foi possível carregar o histórico de operações',
    cancelOrder: 'Cancelar',
    cancelFailed: 'Não foi possível cancelar a ordem',
    closeFailed: 'Não foi possível fechar a posição',
    closeConfirmTitle: 'Fechar a posição?',
    closeConfirmSubtitle:
      'A posição será fechada a preço de mercado e o resultado atual torna-se definitivo. Não é possível anular.',
  },

  chart: {
    title: 'Gráfico',
    interval: 'Intervalo',
    chartType: 'Tipo de gráfico',
    candles: 'Velas',
    line: 'Linha',
    high24h: 'Máx. 24 h',
    low24h: 'Mín. 24 h',
    volume24h: 'Volume 24 h',
    empty: 'O gráfico aparecerá quando houver dados suficientes para este par',
    failed: 'Não foi possível carregar o gráfico',
    unknownPair: 'Esse par não existe: escolha-o no separador «Mercados»',
    toMarkets: 'Para os mercados',
  },

  security: {
    title: 'Segurança',
    subtitle: 'O segundo fator protege o acesso e os levantamentos',
    statusOn: 'Ativado',
    statusOff: 'Não ativado',
    explain:
      'O código da aplicação de autenticação é pedido no início de sessão e em cada pedido de levantamento. Sem ele, a conta fica protegida apenas pela palavra-passe.',
    setup: 'Configurar autenticador',
    step1: 'Digitalize o código no Google Authenticator, 1Password ou Aegis',
    secretLabel: 'Ou introduza o segredo manualmente',
    step2: 'Introduza o código da aplicação',
    enable: 'Ativar',
    enabled: 'Segundo fator ativado',
    disableTitle: 'Desativar o segundo fator',
    disableHint: 'Introduza o código atual para confirmar que o telemóvel está consigo',
    disable: 'Desativar',
    disabled: 'Segundo fator desativado',
    codeInvalid: 'O código não foi aceite. Verifique a hora do telemóvel e introduza um novo',
    failed: 'Não foi possível alterar as definições de segurança',
    withdrawalCode: 'Código de confirmação',
    withdrawalCodeHint: 'O levantamento está protegido pelo segundo fator: introduza o código da aplicação',
    recoveryTitle: 'Códigos de recuperação',
    recoveryIntro: 'Guarde estes códigos num local seguro. Cada um permite um acesso ou um levantamento se o telemóvel não estiver disponível. Não voltarão a ser mostrados.',
    recoveryCopy: 'Copiar códigos',
    recoveryCopied: 'Códigos copiados',
    recoverySaved: 'Guardei os códigos',
    recoveryLeft: (count: number): string => `Códigos de recuperação restantes: ${count}`,
    recoveryRegenerate: 'Novos códigos de recuperação',
    recoveryRegenerateHint: 'Os códigos antigos deixam de funcionar. Introduza o código da aplicação',
    recoveryRegenerated: 'Foram emitidos novos códigos de recuperação',
    codeOrRecovery: 'Os seis dígitos da aplicação ou um código de recuperação',
  },

  addressBook: {
    title: 'Endereços de levantamento',
    hint: 'Um endereço novo passa a funcionar após a confirmação pela ligação do e-mail. Assim, uma sessão alheia não consegue desviar fundos para o seu próprio endereço.',
    add: 'Adicionar endereço',
    address: 'Endereço',
    label: 'Nome',
    labelPlaceholder: 'Por exemplo, Ledger',
    send: 'Confirmar por e-mail',
    saved: 'Endereços guardados',
    empty: 'Ainda não há endereços',
    emptyBody: 'Adicione o endereço para onde levanta: depois de confirmado pelo e-mail, fica a um toque no formulário de levantamento.',
    statusConfirmed: 'confirmado',
    statusPending: 'aguarda confirmação por e-mail',
    resend: 'Reenviar',
    resendLink: 'Reenviar o e-mail de confirmação',
    remove: 'Eliminar endereço',
    removeTitle: 'Eliminar este endereço?',
    removeHint: 'Os levantamentos para este endereço deixam de ser possíveis até o adicionar e confirmar de novo.',
    linkSent: 'E-mail enviado: abra a ligação para confirmar o endereço',
    alreadyConfirmed: 'Este endereço já está confirmado',
    invalid: 'O endereço não corresponde à rede escolhida',
    confirmed: (short: string): string => `Endereço ${short} confirmado`,
    confirmFailed: 'A ligação é inválida ou expirou',
  },

  sessions: {
    title: 'Sessões e dispositivos',
    subtitle: 'Onde a sua conta tem sessão iniciada',
    current: 'Este dispositivo',
    started: 'Sessão iniciada',
    lastSeen: 'Atividade',
    end: 'Terminar',
    endOthers: 'Terminar todas as outras sessões',
    ended: 'Sessão terminada',
    endedOthers: (count: number): string => `Sessões terminadas: ${count}`,
    empty: 'Não há outras sessões ativas',
    failed: 'Não foi possível carregar as sessões',
    hint: 'Uma sessão terminada perde o acesso em quinze minutos: é o tempo de vida de uma chave de acesso já emitida.',
  },

  profile: {
    title: 'Perfil',
    accountId: 'ID da conta',
    idCopied: 'ID copiado',
    loadFailed: 'Não foi possível carregar o perfil',
    partnerProgram: 'Programa de parceiros',
    partnerProgramHint: 'Equipa, nível e recompensas',
    securityOn: 'Segundo fator ativado',
    securityOff: 'Ative o segundo fator',

    verification: 'Verificação',
    verificationNone: 'Não efetuada',
    verificationPending: 'Em análise',
    verificationInReview: 'Em análise',
    verificationApproved: 'Concluída',
    verificationRejected: 'Recusada',
    verificationResubmit: 'São precisos novos documentos',
    verificationCta: 'Começar',
    verificationCtaAgain: 'Enviar novamente',
    verificationPrompt: 'Carregue o passaporte: leva 2 minutos',
    verificationPendingHint: 'O operador costuma analisar os documentos no prazo de um dia.',
    verificationRejectedHint: 'O operador não aceitou os documentos. Pode enviar o pedido novamente.',
    verificationOpen: 'Abrir pedido',

    withdrawalAddressesHint: 'Lista de endereços com confirmação por e-mail',
    withdrawalAddresses: 'Endereços de levantamento',
    addressesSubtitle: 'Endereços para os quais já levantou fundos',
    addressesEmpty: 'Ainda não levantou fundos. O endereço do seu primeiro pedido aparecerá aqui.',
    addressesFailed: 'Não foi possível carregar os endereços',
    addressCopied: 'Endereço copiado',

    security: 'Segurança',
    accountAccess: 'E-mail e palavra-passe',
    sessions: 'Sessões e dispositivos',
    sessionsHint: 'Onde tem sessão iniciada',
    securitySubtitle: 'Acesso à conta',
    securityEmailVerified: 'E-mail confirmado',
    securityEmailUnverified: 'E-mail não confirmado',
    securityPassword: 'Mudar palavra-passe',
    securityPasswordHint: 'Enviaremos por e-mail uma ligação para mudar a palavra-passe',

    notifications: 'Notificações',
    pushOn: 'Push ativado',
    pushOff: 'Push desativado',
    pushToggle: 'Notificações push',
    pushUnsupported: 'Este navegador não suporta notificações push',
    pushDenied: 'As notificações estão bloqueadas nas definições do navegador',
    pushFailed: 'Não foi possível ativar as notificações push',
    pushUnavailable: 'As notificações push ainda não estão configuradas no servidor',


    theme: 'Tema',
    themeHint: 'O tema preto é o da marca e poupa bateria em OLED. O claro é para sol forte. «Como o sistema» segue a definição do telemóvel.',
    themeNames: { system: 'Como o sistema', dark: 'Escuro', light: 'Claro' } as Record<'system' | 'dark' | 'light', string>,
    themeSystemHint: 'Segue a definição do dispositivo',
    language: 'Idioma',
    languageHint: 'Idioma da interface. Os montantes mantêm um formato único; as datas seguem o idioma.',

    support: 'Apoio',
    supportTitle: 'Contactar o apoio',
    supportChoose: 'Escolha como prefere falar connosco',
    supportVia: 'Telegram ou e-mail',
    supportTelegram: 'Escrever no Telegram',
    supportEmail: 'Escrever por e-mail',
    about: 'Sobre a aplicação',
    version: 'Versão',

    signOut: 'Terminar sessão',
    signOutTitle: 'Terminar a sessão?',
    signOutBody:
      'Saldos, posições abertas e histórico de sinais ficam no lugar: volta a encontrá-los no próximo início de sessão. Os outros dispositivos continuam com sessão iniciada.',

    kyc: {
      title: 'Verificação',
      intro:
        'Só o operador de verificação vê estes dados. O número do documento é guardado cifrado e nunca é mostrado por inteiro.',
      loadFailed: 'Não foi possível carregar o pedido',

      document: 'Documento',
      documentType: 'Tipo de documento',
      typePassport: 'Passaporte',
      typeIdCard: 'Cartão de cidadão',
      typeDriverLicense: 'Carta de condução',
      number: 'Número do documento',
      numberPlaceholder: '4510 123456',
      numberHint: 'Série e número, tal como no documento',
      country: 'País de emissão',
      countryPlaceholder: 'Escolha um país',
      issueDate: 'Data de emissão',
      expiryDate: 'Válido até',

      personal: 'Titular do documento',
      lastName: 'Apelido',
      firstName: 'Nome',
      dateOfBirth: 'Data de nascimento',

      documents: 'Fotografia do documento',
      docMain: 'Página principal',
      docMainHint: 'A página com a fotografia e o número',
      docFront: 'Frente',
      docFrontHint: 'O lado com a fotografia e o número',
      choose: 'Escolher ficheiro',
      replace: 'Substituir',
      remove: 'Remover',
      fileHint: (size: string) => `JPG, PNG ou PDF, até ${size} MB`,
      megabytes: 'MB',

      submit: 'Enviar para verificação',
      submitFailed: 'Não foi possível enviar o pedido. Verifique os dados e tente novamente',
      uploadFailed:
        'Os dados foram enviados, mas não foi possível carregar os documentos. Tente anexá-los novamente.',
      uploadRetry: 'Carregar documentos',
      submitted: 'Pedido enviado',
      submittedHint: 'Um operador vai verificar os documentos e atualizar o estado.',

      review: 'Pedido em análise',
      reviewHint: 'Durante a análise, os dados não podem ser alterados.',
      approved: 'Verificação concluída',
      approvedHint: 'O operador confirmou os documentos.',
      rejected: 'Pedido recusado',
      rejectedHint: 'Corrija os dados e envie o pedido novamente.',
      resubmit: 'São precisos novos documentos',
      resubmitHint: 'Carregue um documento válido e envie o pedido novamente.',
      rejectionReason: 'Motivo',
      submittedAt: (date: string) => `Enviado em ${date}`,
      attached: 'Documentos anexados',
      attachedEmpty: 'Nenhum ficheiro anexado ao pedido',
      startNew: 'Enviar novamente',
      step: (current: number, total: number): string => `Passo ${current} de ${total}`,

      gate: {

        title: 'Verifique a sua identidade para usar os fundos',

        body: 'Pode depositar já. Trading, sinais, transferências e levantamentos abrem quando os documentos forem aprovados, normalmente no prazo de um dia.',

        pending: 'Os documentos estão em análise: o acesso abre após a aprovação, normalmente no prazo de um dia.',

        action: 'Fazer a verificação',

      },

      kinds: {
        passport_main: 'Página principal',
        passport_registration: 'Página da morada',
        selfie_with_document: 'Selfie com o documento',
        proof_of_address: 'Comprovativo de morada',
      },

      errors: {
        required: 'Preencha o campo',
        number: 'Apenas letras latinas, algarismos, espaços e hífenes',
        name: 'Apenas letras, espaços, apóstrofos e hífenes',
        country: 'Escolha um país',
        date: 'Indique uma data',
        issueFuture: 'A data de emissão não pode estar no futuro',
        expiryPast: 'O documento expirou',
        expiryBeforeIssue: 'A data de validade deve ser posterior à data de emissão',
        age: 'A verificação está disponível a partir dos 18 anos',
        birthAfterIssue: 'A data de nascimento deve ser anterior à data de emissão',
        fileType: 'Serve um ficheiro JPG, PNG ou PDF',
        fileSize: (size: string) => `O ficheiro excede ${size} MB`,
        documentsRequired: 'Anexe uma fotografia ou digitalização do documento',
        fileMissing: 'Adicione este ficheiro',
      },
    },
  },

  countries: {
    search: 'Nome ou código do país',
    popular: 'Mais frequentes',
    all: 'Todos os países',
    nothing: 'Sem resultados. Experimente o nome em inglês ou um código, por exemplo DE',
    found: (count: number): string => `Países encontrados: ${count}`,
  },

  notifications: {
    promoUnlocked: (amount: string): string => `Bónus promocional de ${amount} USDT desbloqueado e creditado em spot`,
    promoExpired: 'O bónus promocional expirou: as condições não foram cumpridas a tempo',
    referralQualified: (name: string, signals: Count): string =>
      signals.value.greaterThan(0)
        ? `O seu convidado ${name} qualificou-se: +${signals.text} ${pluralEn(signals, 'sinal bónus', 'sinais bónus')}`
        : `O seu convidado ${name} qualificou-se`,
    referralWelcome: (signals: Count, promo: string | null): string => {
      const perks = [
        signals.value.greaterThan(0) ? `+${signals.text} ${pluralEn(signals, 'sinal bónus', 'sinais bónus')}` : null,
        promo === null ? null : `um bónus promocional de ${promo} USDT bloqueado até cumprir a condição`,
      ].filter((perk): perk is string => perk !== null)
      return perks.length === 0 ? 'A sua qualificação por convite está concluída' : `Qualificação concluída: ${perks.join(', ')}`
    },
    referralRevoked: (name: string | null, earlyWithdrawal: boolean): string =>
      name === null
        ? earlyWithdrawal
          ? 'Qualificação revogada: foi pedido um levantamento durante o período de retenção. Os sinais bónus e o bónus promocional foram retirados'
          : 'Qualificação revogada: os sinais bónus e o bónus promocional foram retirados'
        : `A qualificação do seu convidado ${name} foi revogada: os sinais bónus obtidos graças a ele foram retirados`,
    referralRewardPaid: (amount: string): string => `${amount} USDT pagos pelo programa de parceiros`,
    referralRewardRejected: (amount: string): string => `Uma recompensa de ${amount} USDT foi recusada pelo operador`,
    partnerLevelReached: (level: string, amount: string): string =>
      `Nível ${level} alcançado: um bónus de ${amount} USDT aguarda pagamento`,
    title: 'Notificações',
    settingsTitle: 'Definições de notificações',
    settingsHint: 'Uma categoria desativada fica no histórico mas não incomoda: sem distintivo e sem push. O push funciona quando está ativado no perfil.',
    channelInApp: 'Na aplicação',
    channelPush: 'Push',
    channelTelegram: 'Telegram',
    telegramHint: 'O que o bot envia para o seu chat. Uma categoria desligada aqui não chega ao Telegram, mas continua na aplicação.',
    telegramLinked: (name: string): string => (name === '' ? 'Telegram ligado' : `Telegram ligado: ${name}`),
    telegramLinkedHint: 'Se não reconhece esta ligação, toque para a desligar.',
    telegramUnlinked: (name: string): string => (name === '' ? 'Telegram desligado' : `Telegram desligado: ${name}`),
    telegramUnlinkedHint: 'Este chat foi ligado a outra conta.',
    categories: { deposit: 'Depósitos', withdrawal: 'Levantamentos', copy: 'Copy trading', trade: 'Operações e posições', signal: 'Sinais', promo: 'Bónus promocionais', referral: 'Programa de parceiros', kyc: 'Verificação', system: 'Sistema' } as Record<'deposit' | 'withdrawal' | 'copy' | 'trade' | 'signal' | 'promo' | 'referral' | 'kyc' | 'system', string>,
    markAllRead: 'Marcar tudo como lido',
    markAllFailed: 'Não foi possível marcar as notificações como lidas',
    loadFailed: 'Não foi possível carregar as notificações',
    empty:
      'Ainda não há notificações. Aqui aparecerão os créditos, as respostas aos pedidos de levantamento, os sinais e os resultados das operações.',
    unread: 'Não lida',
    today: 'Hoje',
    yesterday: 'Ontem',
    depositCredited: (amount: string, asset: string) =>
      `Creditados ${amount} ${asset}`,
    withdrawalApproved: 'Levantamento aprovado',
    withdrawalRejected: 'Levantamento recusado',
    withdrawalSent: 'Levantamento enviado para a rede',
    marginCall: 'Chamada de margem',
    positionLiquidated: 'Posição liquidada',
    copyOpened: (master: string) => `${master} abriu uma posição`,
    copyClosed: (master: string) => `${master} fechou uma posição`,
    copyStopLoss: 'Stop-loss da subscrição acionado: posições fechadas, fundos devolvidos à conta spot',
    copyMasterStopped: 'Trader parado: a cópia terminou, os fundos regressaram à conta spot',
    copyReleased: (amount: string) => `Devolvido ao spot: ${amount} USDT`,
    copyCommission: (master: string, commission: string, profit: string) =>
      master === '' ? `Comissão para o trader: ${commission} USDT sobre ${profit} USDT de lucro` : `Comissão para ${master}: ${commission} USDT sobre ${profit} USDT de lucro`,
    signalLive: (title: string, until: string) => `Sinal «${title}» aberto até ${until}`,
    futuresLiquidated: (symbol: string, price: string) => `Posição ${symbol} liquidada a ${price}`,
    futuresTakeProfit: (symbol: string, price: string) => `${symbol}: take-profit acionado a ${price}`,
    futuresStopLoss: (symbol: string, price: string) => `${symbol}: stop-loss acionado a ${price}`,
    orderFilled: (symbol: string, quantity: string, price: string) => `Ordem limite ${symbol} executada: ${quantity} a ${price}`,
    orderRejected: (symbol: string) => `Ordem limite ${symbol} não executada: o motor rejeitou a operação`,
    orderRejectedFunds: (symbol: string) => `Ordem limite ${symbol} não executada: margem insuficiente na conta de futuros`,
    orderRejectedLeverage: (symbol: string) => `Ordem limite ${symbol} não executada: a alavancagem excede o limite para este tamanho`,
    kycApproved: 'Verificação concluída',
    kycRejected: 'Verificação recusada',
    kycResubmit: 'A verificação precisa de novos documentos',
    kycExpiring: (date: string) => `O documento expira em ${date}: renove a verificação`,
    kycExpired: 'O documento expirou: carregue um documento válido',
    signalClosed: (title: string) => `Sinal fechado: ${title}`,
    systemAlert: (check: string) => `Incidente: ${check}`,
    systemRecovered: (check: string) => `Restabelecido: ${check}`,
    heads: {
      signalLive: 'Sinal aberto',
      signalClosed: 'Sinal fechado',
      orderFilled: 'Ordem limite executada',
      orderRejected: 'Ordem limite não executada',
      takeProfit: 'Take-profit acionado',
      stopLoss: 'Stop-loss acionado',
      promoUnlocked: 'Bónus promocional desbloqueado',
      promoExpired: 'Bónus promocional expirado',
      referralQualified: 'Convidado qualificado',
      referralWelcome: 'Qualificação concluída',
      referralRevoked: 'Qualificação revogada',
      rewardPaid: 'Recompensa paga',
      rewardRejected: 'Recompensa recusada',
      partnerLevelReached: 'Novo nível de parceiro',
      kycExpiring: 'Documento a expirar',
      kycExpired: 'Documento expirado',
      systemAlert: 'Incidente',
      systemRecovered: 'Restabelecido',
    },
    fallback: {
      deposit: 'Depósito creditado',
      withdrawal: 'Atualização do pedido de levantamento',
      trade: 'Evento na posição',
      copy: 'Evento de copy trading',
      referral: 'Evento do programa de parceiros',
      system: 'Notificação do sistema',
    },
  },

  referrals: {
    title: 'Programa de parceiros',
    rewardKind: {
      fee_rebate: 'Parte da taxa',
      signal_grant: 'Sinais adicionais',
      promotional_credit: 'Bónus promocional',
      deposit_bonus: 'Parte do depósito',
      welcome_bonus: 'Parte de boas-vindas',
      level_bonus: 'Bónus de nível',
    },
    rewardStatus: {
      pending_review: 'Em análise',
      approved: 'Aprovada',
      paid: 'Paga',
      rejected: 'Recusada',
      reversed: 'Anulada',
    },
    /** The promo grant's own state, shown on its reward row instead of the review status. */
    promoState: {
      locked: 'Bloqueado',
      unlocked: 'Creditado',
      expired: 'Expirou',
      revoked: 'Revogado',
    },
    /** Level names by `level.code`; an unknown code falls back to the name the server sends. */
    levelName: {
      member: 'Membro',
      bronze: 'Bronze',
      silver: 'Prata',
      gold: 'Ouro',
      platinum: 'Platina',
      ambassador: 'Embaixador',
    } as Record<string, string>,
    signalUnit: 'sinais',
    membersFrom: (count: Count): string => `a partir de ${count.text} ${pluralEn(count, 'membro', 'membros')}`,
    baseLevel: 'nível inicial',
    depositFrom: (amount: string): string => `depósito a partir de ${amount}`,
    noWeeklySignals: 'sem sinais',
    perWeek: (count: Count): string => `${count.text} ${pluralEn(count, 'sinal', 'sinais')}/sem.`,
    current: 'atual',
    copyFailed: 'Não foi possível copiar',
    inviteText: 'Junte-se à 19CRT com o meu código',
    linkCopied: 'Ligação copiada',
    codeCopied: 'Código copiado',
    loadError: 'Não foi possível carregar o programa de parceiros',
    loadErrorHint: 'Verifique a sua ligação à internet e tente novamente',
    retry: 'Tentar novamente',
    disabled:
      'O programa de parceiros está temporariamente suspenso. As suas estatísticas e recompensas ficam guardadas.',
    yourCode: 'O seu código',
    codeHint: 'Convide diretamente: as condições são visíveis antes do registo',
    openQr: 'Abrir o código QR de convite',
    qrCode: 'Código QR',
    share: 'Partilhar',
    copy: 'Copiar',
    yourLevel: 'O seu nível',
    participant: 'Membro',
    allLevels: 'Todos os níveis',
    progressLabel: 'Progresso para o próximo nível',
    qualified: (count: Count): string => pluralEn(count, 'qualificado', 'qualificados'),
    nextLevel: (remaining: Count, level: string): string => `${pluralEn(remaining, 'falta', 'faltam')} ${remaining.text} para o nível ${level}`,
    maxLevel: 'nível máximo',
    invited: 'Convidados',
    confirmed: 'Confirmados',
    paidUsdt: 'Pago, USDT',
    signalsReceived: 'Sinais recebidos',
    transparentTitle: 'Recompensa transparente',
    transparentHint:
      'A recompensa é calculada apenas sobre a taxa de trading efetivamente cobrada. O depósito do convidado não é redistribuído.',
    rewards: 'Recompensas',
    rewardHistory: 'Histórico de recompensas',
    underReview: 'Em análise',
    emptyRewardsTitle: 'Ainda sem recompensas',
    emptyRewards: 'As recompensas aparecerão após a primeira atividade qualificada da sua equipa.',
    levelsRules: 'Níveis e regras',
    levelsRulesHint: 'Equipa, parte da taxa e sinais',
    inviteTitle: 'Convite para a 19CRT',
    inviteSubtitle: 'O código é associado no registo',
    copyLink: 'Copiar ligação',
    levelsTitle: 'Níveis de parceiro',
    levelsSubtitle: 'Só contam os membros diretos qualificados',
    loadLevelsError: 'Não foi possível carregar os níveis',
    levelTeam: 'Nível e equipa',
    feeSignals: 'Taxa · sinais',
    depositTiersTitle: 'Percentagens por depósito',
    depositTiersHint: 'Quanto maior o primeiro depósito, maior a parte para ambos',
    tierDeposit: 'Depósito',
    tierReferrer: 'Para si',
    tierWelcome: 'Novo membro',
    tierFrom: (amount: string): string => `a partir de ${amount}`,
    levelBonus: 'Bónus de nível',
    /** The programme's rules, worded from the operator's live settings — never a copy. */
    rules: {
      title: 'Regras do programa',
      qualification: (amount: string, asset: string): string =>
        `Qualificação: um primeiro depósito creditado de pelo menos ${amount} ${asset} numa única transação.`,
      qualificationKyc: 'É necessária a verificação de identidade.',
      welcome: (signals: Count, days: Count): string =>
        `O novo membro recebe ${signals.text} ${pluralEn(signals, 'sinal bónus válido', 'sinais bónus válidos')} por ${days.text} ${pluralEn(days, 'dia', 'dias')}.`,
      referrer: (signals: Count, days: Count): string =>
        `O parceiro recebe ${signals.text} ${pluralEn(signals, 'sinal bónus válido', 'sinais bónus válidos')} por ${days.text} ${pluralEn(days, 'dia', 'dias')} por cada convidado qualificado.`,
      promo: (amount: string, required: string, days: Count): string =>
        `Um bónus promocional de ${amount} USDT fica bloqueado e torna-se dinheiro em spot quando o volume negociado pelo novo membro atinge ${required} USDT em ${days.text} ${pluralEn(days, 'dia', 'dias')}; caso contrário, expira.`,
      hold: (days: Count): string =>
        `Qualquer pedido de levantamento no prazo de ${days.text} ${pluralEn(days, 'dia', 'dias')} após a qualificação anula-a, juntamente com os sinais bónus e o bónus promocional.`,
      feeShare:
        'A quota de taxa do nível é calculada apenas sobre as taxas de negociação efetivamente cobradas ao convidado. As recompensas em dinheiro são verificadas pelo operador e pagas à parte — nunca são creditadas no saldo.',
      secondLevel: (rate: string): string => `O parceiro do parceiro recebe ${rate} da mesma taxa.`,
      depositTiers:
        'A parte de quem convida e a do novo membro dependem do valor do primeiro depósito — veja a tabela nesta janela.',
      balanceShare: (percent: string): string =>
        `O depósito tem de ser pelo menos ${percent} % do saldo de quem convida no momento do crédito.`,
      topup: (days: Count): string =>
        `Um depósito adicional no prazo de ${days.text} ${pluralEn(days, 'dia', 'dias')} após o primeiro soma-se a este, o par é reavaliado pela tabela e a diferença é paga uma única vez.`,
      premiumSignal: (count: string): string =>
        `O acesso permanente ao sinal premium abre a partir de ${count} convidados qualificados.`,
      antiabuse:
        'Um dispositivo ou endereço partilhado com o parceiro ou com a sua equipa envia a qualificação para análise do operador.',
      paused: 'O programa está suspenso pelo operador: não são criadas novas qualificações nem recompensas.',
    },
    disclaimer:
      'As recompensas de parceiro não são rendimento garantido. O registo, os depósitos e o volume negociado não criam, por si só, qualquer pagamento em dinheiro.',
    team: 'Equipa',
    teamStatus: {
      pending: 'aguarda um depósito',
      flagged: 'em análise pelo operador',
      qualified: 'qualificado',
      revoked: 'qualificação revogada',
    },
    joined: (date: string): string => `desde ${date}`,
    showAll: (count: Count): string => `Mostrar todos (${count.text})`,
    showLess: 'Mostrar menos',
    promoTitle: 'Bónus promocional',
    promoLocked: (amount: string): string => `${amount} USDT bloqueados`,
    promoUnlocked: (amount: string): string => `${amount} USDT creditados em spot`,
    promoExpired: 'Prazo expirado, o bónus perdeu-se',
    promoRevoked: 'Bónus revogado',
    promoCondition: (traded: string, required: string): string => `Volume ${traded} / ${required} USDT`,
    promoUntil: (date: string): string => `até ${date}`,
    promoHint: 'O bónus torna-se dinheiro quando o volume negociado atinge a condição antes do prazo.',
  },

  signals: {
    title: 'Sinais',
    tabs: { today: 'Hoje', history: 'Histórico' },
    tabsLabel: 'Secções de sinais',
    access: { public: 'Público', base: 'Base', premium: 'Premium', bonus: 'Bónus', agent: 'Parceiro' },
    status: { scheduled: 'Agendado', available: 'Disponível', executed: 'Executado', expired: 'Expirado', cancelled: 'Cancelado' },
    side: { buy: 'Compra', sell: 'Venda' },
    direction: { buy: 'Long · compra', sell: 'Short · venda' },
    positionSide: { buy: 'Long', sell: 'Short' },
    localTime: 'hora local',
    localClock: 'Hora local',
    timeZone: (offset: string) => `Fuso horário ${offset}`,
    reason: {
      unavailable: 'Este sinal não está disponível de momento',
      already: 'Já executou este sinal',
      expired: 'A janela de execução expirou',
      notReleased: 'Este sinal ainda não abriu',
      cancelled: 'Este sinal foi cancelado',
      balance: 'Fundos insuficientes na conta spot',
      balanceFutures: 'USDT insuficientes nas contas spot e de futuros em conjunto',
      positionConflict: 'Tem uma posição aberta neste mercado no sentido oposto: feche-a para executar o sinal',
      price: 'O preço deste mercado não está disponível de momento: tente novamente dentro de alguns segundos',
      access: 'Este sinal requer outro nível de acesso',
      kyc: 'Conclua primeiro a verificação: depois de os documentos serem aprovados, poderá executar o sinal',
      minimum: 'Este sinal usa uma parte do seu saldo total, que fica abaixo do mínimo. Carregue o saldo para o executar',
      maximum: 'O montante excede o máximo deste sinal',
      belowMinimum: (amount: string) => `Este sinal usa uma parte do seu saldo total, que fica abaixo do mínimo de ${amount}\u00A0USDT. Carregue o saldo para o executar`,
      aboveMaximum: (amount: string) => `O montante excede o máximo deste sinal: no máximo ${amount} USDT`,
      notFound: 'Nenhum sinal encontrado para este código',
      generic: 'Não foi possível concluir a ação. Verifique as condições e tente novamente',
      network: 'Não foi possível contactar o servidor. Verifique a ligação e tente novamente',
    },
    enterCode: 'Introduzir código',
    /** The explanation line of a signal card: an open window, and a signal already executed. */
    cardHint: { available: 'Introduza o código do canal para ver a direção, o montante e as condições antes de confirmar', executed: 'O resultado e os parâmetros reais estão no separador Histórico', scheduled: (time: string) => `O código aparecerá no canal ${atHour(time)}` },
    executing: 'A executar',
    notExecuted: 'Não executado',
    executedLabel: 'Executado',
    loadSignalsError: 'Não foi possível carregar os sinais',
    noSignalsToday: 'Sem sinais hoje',
    loadErrorHint: 'Verifique a ligação e tente novamente',
    retry: 'Tentar novamente',
    signalAvailable: 'Sinal disponível',
    nextSignal: 'Próximo sinal',
    windowOpenUntil: (time: string) => `Janela aberta até ${atHour(time)}`,
    opensAt: (time: string) => `Abre ${atHour(time)}`,
    execute: 'Executar',
    doneToday: 'Por hoje é tudo',
    newBySchedule: 'Os novos sinais aparecerão segundo o horário',
    codeOverline: 'Código do sinal',
    checkTerms: 'Verificar condições',
    codeHint: 'Cada código é publicado no nosso canal de Telegram: introduza-o assim que o vir.',
    openChannel: 'Abrir o canal',
    channelLink: 'canal de Telegram',
    panelTitle: 'Código da operação',
    dealsTitle: 'Operações por código',
    dealsEmpty: 'Ainda não há operações por código',
    dealsEmptyHint: 'Introduza um código do canal: a operação e o resultado aparecem aqui',
    codePlaceholder: 'Por exemplo, 1QF9ET7RQ',
    codeRequired: 'Introduza o código do canal',
    codeLabel: 'Código do sinal',
    check: 'Verificar',
    schedule: 'Horário',
    todaySignals: 'Sinais de hoje',
    noSignals: 'Não há sinais neste momento. Os novos códigos saem segundo o horário no nosso canal de Telegram.',
    riskDisclosure:
      'Um sinal é uma ideia de trading, não um resultado garantido. Antes de confirmar verá a direção, o montante, a alavancagem, a taxa e as condições de fecho.',
    loadHistoryError: 'Não foi possível carregar o histórico de sinais',
    historyEmpty: 'O histórico está vazio',
    historyEmptyHint: 'Aqui aparecerão os sinais executados e os seus parâmetros reais.',
    toSignals: 'Para os sinais',
    allExecutions: 'Todas as execuções',
    signalAccepted: 'Sinal aceite',
    confirmation: 'Confirmação do sinal',
    sentToTrading: 'A ordem foi enviada para o sistema de trading',
    checkBeforeExecute: 'Verifique as condições antes da execução',
    acceptedHint: 'Tocar novamente não cria uma segunda ordem.',
    ready: 'Concluído',
    account: 'Conta',
    spot: 'Spot',
    futures: 'Futuros',
    perpetualMarket: 'futuros',
    validUntil: 'Validade',
    signalName: 'Sinal',
    until: (time: string) => `até ${atHour(time)}`,
    timeLeft: (countdown: string) => `restam ${countdown}`,
    priceGuide: 'Preço indicativo',
    entryGuide: 'Entrada indicativa',
    marketPrice: 'Mercado',
    stopLoss: 'Stop-loss',
    takeProfit: 'Take-profit',
    autoClose: 'Fecho automático',
    inDuration: (duration: string) => `dentro de ${duration}`,
    duration: (days: number, hours: number, minutes: number): string =>
      [days > 0 ? `${days}\u00A0d` : null, hours > 0 ? `${hours}\u00A0h` : null, minutes > 0 || (days === 0 && hours === 0) ? `${minutes}\u00A0min` : null]
        .filter((part): part is string => part !== null)
        .join(' '),
    alreadyExecuted: 'Já executado',
    executionUnavailable: 'Execução indisponível',
    executionAmount: 'Montante da execução',
    marginAmount: 'Margem',
    /** Where the amount comes from: « 1% общего баланса · 997.64 USDT ». */
    shareOfBalance: (share: string, total: string) => `${share} do saldo total · ${total}\u00A0USDT`,
    estimatedFee: 'Taxa estimada',
    totalDebit: 'Total a debitar',
    positionSize: 'Tamanho da posição',
    willSell: 'A vender',
    fundingNote: (amount: string) =>
      `Para esta operação, ${amount} USDT passarão da conta spot para a conta de futuros. Quando a posição fechar, esse montante, ajustado pelo resultado, regressa à conta spot.`,
    executionRisk:
      'Um sinal é uma ideia de trading, não um resultado garantido. O preço de execução pode diferir do estimado e a operação pode dar prejuízo. Confirme apenas um risco que compreenda.',
    executionRiskFutures:
      'Um sinal é uma ideia de trading, não um resultado garantido. O preço de entrada pode diferir do estimado e a alavancagem amplifica tanto o ganho como a perda, até à liquidação da posição. Confirme apenas um risco que compreenda.',
    retryExecution: 'Repetir a execução',
    outcomeLabel: 'resultado, USDT',
    outcomeOpen: 'em curso',
    outcome: { take_profit: 'fechado no alvo', stop_loss: 'fechado no stop', time: 'fechado por tempo', manual: 'fechado manualmente', liquidation: 'fechado por liquidação' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    outcomeBadge: { take_profit: 'No alvo', stop_loss: 'No stop', time: 'Por tempo', manual: 'Manual', liquidation: 'Liquidado' } as Record<'take_profit' | 'stop_loss' | 'time' | 'manual' | 'liquidation', string>,
    openBadge: 'Em curso',
    executeFor: 'Executar até',
    openLong: (amount: string) => `Abrir long · ${amount} USDT`,
    openShort: (amount: string) => `Abrir short · ${amount} USDT`,
    submittedToast: 'Sinal enviado para execução',
    filledOf: (requested: string, filled: string) =>
      `Pedido ${requested} USDT, executado ${filled} USDT: a quantidade foi arredondada ao passo do lote.`,
    marginRounded: (requested: string) => `Pedido ${requested} USDT: a quantidade foi arredondada ao passo do lote.`,
    longOpened: (margin: string, leverage: number) => `Long aberto: margem de ${margin} USDT, alavancagem ${leverage}×.`,
    shortOpened: (margin: string, leverage: number) => `Short aberto: margem de ${margin} USDT, alavancagem ${leverage}×.`,
    fundedFromSpot: (amount: string) => `${amount} USDT transferidos da conta spot.`,
    closesBy: (takeProfit: boolean, stopLoss: boolean, duration: string | null): string => {
      const levels = [takeProfit ? 'no take-profit' : null, stopLoss ? 'no stop-loss' : null].filter((level): level is string => level !== null)
      if (levels.length === 0) return duration === null ? '' : `Fecha automaticamente dentro de ${duration}`
      const exits = duration === null ? levels : [...levels, `dentro de ${duration}`]
      const last = exits[exits.length - 1] ?? ''
      return `Fecha ${exits.length === 1 ? last : `${exits.slice(0, -1).join(', ')} ou ${last}`}`
    },
    openPosition: 'Ir para a posição',
    returnedToSpot: (amount: string) => `Devolvido ao spot: ${amount} USDT`,
    positionAccepted: 'A posição está aberta na conta de futuros',
  },

  notFound: {
    title: 'Página não encontrada',
    message: 'Esta página não existe: a ligação está desatualizada ou o endereço tem uma gralha.',
    action: 'Para o início',
  },
}
